import React, { useState, useEffect, useRef, useId } from 'react';
import {
  BookOpen, Plus, Edit3, Trash2, Save, X, ChevronUp, ChevronDown,
  Layers, FileQuestion, CheckCircle2, AlertTriangle, Info, FilePlus, Loader2, ShieldAlert,
  Download, Sparkles, LogIn, LogOut, Terminal, Play, HelpCircle, Cloud, Check, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import pdfToText from 'react-pdftotext';
import mammoth from 'mammoth';
import vortexDocsService from '../../services/vortexDocsService';
import {
  getAcademyStructure, watchTemas, saveTema, deleteTema, emptyLesson, emptyPdfResource,
  getFirestoreAdminStatus, importAllFromRTDB, generatePaginaConIA,
  watchAreaTemas, saveAreaTema, deleteAreaTema,
  importAreasFromRTDB, generateAreaPaginaConIA,
  generateTemasYLeccionesConIA, saveGeneratedTemasToFirestore,
  generateInglesPaginaConIA, AREA_NAMES,
} from '../../services/academyFirestore';
import {
  startGeneration, getTaskStatus, getGeneratorHealth, resetGeneratorQuota,
  getNotebooklmStatus, notebooklmLogin, notebooklmLogout, getNotebooklmLogs,
  buildUnitPayload, getTemaUnits,
} from '../../services/notebooklmService';
import { FlashcardsEditor } from './FlashcardsEditor';
import { R2ObjectPicker } from './R2ObjectPicker';

// Áreas transversales del EXANI-II disponibles en el lector. Los keys son los
// definidos en src/constants/exaniAreas.js.
const GENERAL_AREAS = [
  { key: 'pensamiento_matematico', label: '🧮 Pensamiento Matemático', color: '#38bdf8' },
  { key: 'comprension_lectora', label: '📖 Comprensión Lectora', color: '#c084fc' },
  { key: 'redaccion_indirecta', label: '✍️ Redacción Indirecta', color: '#34d399' },
  { key: 'ingles', label: '🇬🇧 Inglés', color: '#f59e0b' },
];

const S = {
  card: {
    background: 'rgba(10, 25, 41, 0.6)', border: '1px solid rgba(197,160,89,0.18)',
    borderRadius: '14px', overflow: 'hidden',
  },
  label: {
    fontSize: '0.7rem', fontWeight: 700, color: '#c5a059', textTransform: 'uppercase',
    letterSpacing: '0.06em', marginBottom: '4px', display: 'block',
  },
  input: {
    width: '100%', background: 'rgba(7, 21, 38, 0.8)', border: '1px solid rgba(197,160,89,0.25)',
    borderRadius: '8px', padding: '8px 12px', fontSize: '0.8rem', color: '#e2e8f0',
    outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif",
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #c5a059 0%, #8a6a2e 100%)', color: '#fff', border: 'none',
    borderRadius: '10px', padding: '9px 18px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnSecondary: {
    background: 'rgba(197,160,89,0.1)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.3)',
    borderRadius: '10px', padding: '8px 14px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.1)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px', padding: '8px 12px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap',
  },
  badge: (color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 9px', borderRadius: '20px',
    fontSize: '0.65rem', fontWeight: 700, background: `${color}22`, color, border: `1px solid ${color}44`,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  }),
};

const ModalShell = ({ title, icon, onClose, onSubmit, submitLabel, children }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    background: 'rgba(4,12,22,0.85)', backdropFilter: 'blur(10px)',
  }}>
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
      style={{
        background: '#0a1929', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '18px',
        width: '100%', maxWidth: '860px', maxHeight: '92vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 22px', borderBottom: '1px solid rgba(197,160,89,0.15)',
        position: 'sticky', top: 0, background: '#0a1929', zIndex: 1,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(197,160,89,0.12)', border: '1px solid rgba(197,160,89,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {icon}
          </div>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#fff' }}>{title}</h3>
        </div>
        <button type="button" onClick={onClose}  className="adm-btn adm-btn--secondary">
          <X size={20} />
        </button>
      </div>

      <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {children}
      </div>

      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: '10px',
        padding: '16px 22px', borderTop: '1px solid rgba(197,160,89,0.15)',
        position: 'sticky', bottom: 0, background: '#0a1929',
      }}>
        <button type="button" onClick={onClose} style={S.btnSecondary}>Cancelar</button>
        <button type="submit" style={S.btnPrimary}>
          <Save size={14} />
          <span>{submitLabel}</span>
        </button>
      </div>
    </form>
  </div>
);

// ── Reanudación de generaciones audiovisuales pendientes ──
// Si el admin cierra/navega la página durante la generación (~10 min), el polling
// del frontend se detiene y los links no se guardan. Guardamos la tarea en
// localStorage al iniciar y la reaplicamos al volver a montar el panel.
const PENDING_GEN_KEY = 'vortex_pending_audiovisual_generations';
const getPendingGenerations = () => {
  try {
    const raw = localStorage.getItem(PENDING_GEN_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};
const storePendingGeneration = (rec) => {
  const list = getPendingGenerations();
  list.push(rec);
  try { localStorage.setItem(PENDING_GEN_KEY, JSON.stringify(list)); } catch { /* ignore */ }
};
const removePendingGeneration = (taskId) => {
  const list = getPendingGenerations().filter((r) => r.taskId !== taskId);
  try { localStorage.setItem(PENDING_GEN_KEY, JSON.stringify(list)); } catch { /* ignore */ }
};

/* ── Editor de una lección de lectura ── */
const LessonEditor = ({ lesson, onChange }) => {
  // ids únicos por instancia (accesibilidad: label ↔ input)
  const uid = useId();
  // Selector de objetos R2: null | { target: 'video' | 'audio' | 'image:<i>' | 'pdf:<i>' }
  const [picker, setPicker] = useState(null);
  // Firebase RTDB no persiste arreglos vacíos ([] → null): siempre normalizamos.
  const paragraphs = lesson.paragraphs || [];
  const bullets = lesson.bullets || [];
  const kps = lesson.kps || [];
  const quizQuestions = lesson.quizQuestions || [];
  const flashcards = lesson.flashcards || [];
  const pdfResources = lesson.pdf_resources || [];
  const images = lesson.images || [];

  const set = (patch) => onChange({ ...lesson, paragraphs, bullets, kps, quizQuestions, flashcards, pdfResources, images, ...patch });

  const updateQuizQuestion = (qIdx, patch) => {
    const q = quizQuestions[qIdx];
    if (patch.optionsText !== undefined) {
      patch.options = patch.optionsText.split('\n').map((o) => o.trim()).filter(Boolean);
      delete patch.optionsText;
    }
    const updated = { ...q, ...patch };
    const options = updated.options || [];
    let correct_index = parseInt(updated.correct_index, 10);
    if (Number.isNaN(correct_index) || correct_index < 0 || correct_index >= options.length) correct_index = 0;
    const list = [...quizQuestions];
    list[qIdx] = { ...updated, options, correct_index };
    set({ quizQuestions: list });
  };

  return (
    <div style={{
      border: '1px solid rgba(197,160,89,0.25)', borderRadius: '12px', padding: '16px',
      background: 'rgba(7,21,38,0.4)', display: 'flex', flexDirection: 'column', gap: '12px',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={S.label} htmlFor={`${uid}-title`}>Título de la lección</label>
          <input id={`${uid}-title`} type="text" value={lesson.title} onChange={(e) => set({ title: e.target.value })} style={S.input} placeholder="Lección 1: ..." />
        </div>
        <div>
          <label style={S.label} htmlFor={`${uid}-module`}>Módulo</label>
          <input id={`${uid}-module`} type="text" value={lesson.module} onChange={(e) => set({ module: e.target.value })} style={S.input} placeholder="Módulo 1" />
        </div>
      </div>

      <div>
        <label style={S.label} htmlFor={`${uid}-paragraphs`}>Párrafos (uno por línea)</label>
        <textarea
          id={`${uid}-paragraphs`}
          rows={3}
          value={paragraphs.join('\n')}
          onChange={(e) => set({ paragraphs: e.target.value.split('\n').map((p) => p.trim()).filter(Boolean) })}
          style={{ ...S.input, resize: 'vertical' }}
          placeholder={'La aplicación del Artículo 15...\nToda intervención debe documentarse...'}
        />
      </div>

      <div>
        <label style={S.label} htmlFor={`${uid}-bullets`}>Conceptos clave / bullets (uno por línea)</label>
        <textarea
          id={`${uid}-bullets`}
          rows={3}
          value={bullets.join('\n')}
          onChange={(e) => set({ bullets: e.target.value.split('\n').map((b) => b.trim()).filter(Boolean) })}
          style={{ ...S.input, resize: 'vertical' }}
          placeholder={'Principio de proporcionalidad...\nDocumentación obligatoria...'}
        />
      </div>

      {/* Material de apoyo: video + audio + PDFs + imágenes */}
      <div>
        <label style={S.label} htmlFor={`${uid}-video`}>Video de apoyo (URL de YouTube o .mp4)</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id={`${uid}-video`}
            type="text"
            value={lesson.video_url || ''}
            onChange={(e) => set({ video_url: e.target.value })}
            style={{ ...S.input, flex: 1 }}
            placeholder="https://www.youtube.com/embed/... o https://pub-...r2.dev/...mp4"
          />
          <button
            type="button"
            onClick={() => setPicker({ target: 'video' })}
            style={{ ...S.btnSecondary, padding: '0 12px', flexShrink: 0 }}
            title="Elegir archivo subido al bucket R2"
          >
            <Cloud size={13} /> R2
          </button>
        </div>
      </div>

      <div>
        <label style={S.label} htmlFor={`${uid}-audio`}>Podcast / audio de apoyo (mp3)</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id={`${uid}-audio`}
            type="text"
            value={lesson.audio_url || ''}
            onChange={(e) => set({ audio_url: e.target.value })}
            style={{ ...S.input, flex: 1 }}
            placeholder="https://.../podcast.mp3 o https://pub-...r2.dev/...m4a"
          />
          <button
            type="button"
            onClick={() => setPicker({ target: 'audio' })}
            style={{ ...S.btnSecondary, padding: '0 12px', flexShrink: 0 }}
            title="Elegir archivo subido al bucket R2"
          >
            <Cloud size={13} /> R2
          </button>
        </div>
        <input
          id={`${uid}-audio-caption`}
          type="text"
          value={lesson.audio_caption || ''}
          onChange={(e) => set({ audio_caption: e.target.value })}
          style={{ ...S.input, marginTop: '8px' }}
          placeholder="Título / encabezado del podcast (opcional)"
        />
      </div>

      {/* Infografía (generada por NotebookLM) */}
      <div>
        <label style={S.label} htmlFor={`${uid}-infographic`}>Infografía (URL de imagen generada)</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id={`${uid}-infographic`}
            type="text"
            value={lesson.infographic_url || ''}
            onChange={(e) => set({ infographic_url: e.target.value })}
            style={{ ...S.input, flex: 1 }}
            placeholder="https://pub-...r2.dev/.../infographic.png"
          />
          <button
            type="button"
            onClick={() => setPicker({ target: 'infographic' })}
            style={{ ...S.btnSecondary, padding: '0 12px', flexShrink: 0 }}
            title="Elegir archivo subido al bucket R2"
          >
            <Cloud size={13} /> R2
          </button>
        </div>
        {lesson.infographic_url && (
          <img
            src={lesson.infographic_url}
            alt="Infografía de la lección"
            style={{ marginTop: '8px', maxWidth: '100%', maxHeight: '220px', objectFit: 'contain', borderRadius: '10px', border: '1px solid rgba(197,160,89,0.25)', background: '#0f2035' }}
          />
        )}
      </div>

      {/* Presentación de diapositivas (generada por NotebookLM) */}
      <div>
        <label style={S.label} htmlFor={`${uid}-slides`}>Presentación de diapositivas (URL del PDF generado)</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id={`${uid}-slides`}
            type="text"
            value={lesson.slides_url || ''}
            onChange={(e) => set({ slides_url: e.target.value })}
            style={{ ...S.input, flex: 1 }}
            placeholder="https://pub-...r2.dev/.../slides.pdf"
          />
          <button
            type="button"
            onClick={() => setPicker({ target: 'slides' })}
            style={{ ...S.btnSecondary, padding: '0 12px', flexShrink: 0 }}
            title="Elegir archivo subido al bucket R2"
          >
            <Cloud size={13} /> R2
          </button>
        </div>
        {lesson.slides_url && (
          <a
            href={lesson.slides_url}
            target="_blank"
            rel="noreferrer"
            style={{ ...S.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '8px', textDecoration: 'none' }}
          >
            📑 Ver presentación (PDF)
          </a>
        )}
      </div>

      <div>
        <label style={S.label}>Imágenes de apoyo (URL + pie opcional)</label>
        {images.map((im, ii) => (
          <div key={ii} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
            <input
              id={`${uid}-img-url-${ii}`}
              aria-label={`URL de la imagen de apoyo ${ii + 1}`}
              type="text" value={im.url || ''} onChange={(e) => {
                const list = [...images];
                list[ii] = { ...list[ii], url: e.target.value };
                set({ images: list });
              }} style={{ ...S.input, flex: 1 }} placeholder="https://.../imagen.png o https://pub-...r2.dev/...png" />
            <button
              type="button"
              onClick={() => setPicker({ target: `image:${ii}` })}
              style={{ ...S.btnSecondary, padding: '0 10px', flexShrink: 0 }}
              title="Elegir archivo subido al bucket R2"
            >
              <Cloud size={13} />
            </button>
            <input
              id={`${uid}-img-cap-${ii}`}
              aria-label={`Pie de la imagen de apoyo ${ii + 1}`}
              type="text" value={im.caption || ''} onChange={(e) => {
                const list = [...images];
                list[ii] = { ...list[ii], caption: e.target.value };
                set({ images: list });
              }} style={{ ...S.input, width: '220px' }} placeholder="Pie / descripción" />
            <button type="button" onClick={() => set({ images: images.filter((_, x) => x !== ii) })} style={{ ...S.btnDanger, padding: '6px 10px' }}>
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => set({ images: [...images, { url: '', caption: '' }] })} style={S.btnSecondary}>
          <FilePlus size={12} /> <span>Agregar imagen</span>
        </button>
      </div>

      <div>
        <label style={S.label}>Material en PDF (manual, reglamento, formato...)</label>
        {pdfResources.map((pdf, pi) => (
          <div key={pdf.id || pi} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
            <input
              id={`${uid}-pdf-name-${pi}`}
              aria-label={`Nombre del PDF ${pi + 1}`}
              type="text" value={pdf.name} onChange={(e) => {
                const list = [...pdfResources];
                list[pi] = { ...list[pi], name: e.target.value };
                set({ pdf_resources: list });
              }} style={{ ...S.input, flex: 1 }} placeholder="Nombre visible" />
            <input
              id={`${uid}-pdf-url-${pi}`}
              aria-label={`URL del PDF ${pi + 1}`}
              type="text" value={pdf.url} onChange={(e) => {
                const list = [...pdfResources];
                list[pi] = { ...list[pi], url: e.target.value };
                set({ pdf_resources: list });
              }} style={{ ...S.input, flex: 1 }} placeholder="https://.../archivo.pdf o https://pub-...r2.dev/...pdf" />
            <button
              type="button"
              onClick={() => setPicker({ target: `pdf:${pi}` })}
              style={{ ...S.btnSecondary, padding: '0 10px', flexShrink: 0 }}
              title="Elegir archivo subido al bucket R2"
            >
              <Cloud size={13} />
            </button>
            <button type="button" onClick={() => set({ pdf_resources: pdfResources.filter((_, x) => x !== pi) })} style={{ ...S.btnDanger, padding: '6px 10px' }}>
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => set({ pdf_resources: [...pdfResources, emptyPdfResource()] })} style={S.btnSecondary}>
          <FilePlus size={12} /> <span>Agregar PDF</span>
        </button>
      </div>

      {/* Callout */}
      <div>
        <label style={S.label}>Callout (aviso destacado)</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
          {[null, 'info', 'warn'].map((t) => (
            <button
              key={t || 'none'}
              type="button"
              onClick={() => set({ callout: t ? { type: t, icon: t === 'info' ? 'ℹ️' : '⚠️', text: lesson.callout?.text || '' } : null })}
              style={{
                ...(lesson.callout?.type === t || (!lesson.callout && t === null) ? S.btnSecondary : S.btnDanger),
                padding: '6px 12px',
              }}
            >
              {t === 'info' && <Info size={12} />}
              {t === 'warn' && <AlertTriangle size={12} />}
              <span>{t === null ? 'Sin callout' : t === 'info' ? 'Info' : 'Aviso'}</span>
            </button>
          ))}
        </div>
        {lesson.callout && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input id={`${uid}-callout-icon`} aria-label="Icono del callout" type="text" value={lesson.callout.icon} onChange={(e) => set({ callout: { ...lesson.callout, icon: e.target.value } })} style={{ ...S.input, width: '64px' }} placeholder="ℹ️" />
            <input id={`${uid}-callout-text`} aria-label="Texto del callout" type="text" value={lesson.callout.text} onChange={(e) => set({ callout: { ...lesson.callout, text: e.target.value } })} style={S.input} placeholder="Texto del callout..." />
          </div>
        )}
      </div>

      {/* Puntos clave expandibles */}
      <div>
        <label style={S.label}>Puntos clave expandibles (pregunta / respuesta)</label>
        {kps.map((kp, ki) => (
          <div key={ki} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              id={`${uid}-kp-t-${ki}`}
              aria-label={`Punto clave ${ki + 1}, pregunta`}
              type="text" value={kp.t} onChange={(e) => {
                const list = [...kps];
                list[ki] = { ...list[ki], t: e.target.value };
                set({ kps: list });
              }} style={S.input} placeholder="Pregunta / título" />
            <input
              id={`${uid}-kp-b-${ki}`}
              aria-label={`Punto clave ${ki + 1}, respuesta`}
              type="text" value={kp.b} onChange={(e) => {
                const list = [...kps];
                list[ki] = { ...list[ki], b: e.target.value };
                set({ kps: list });
              }} style={S.input} placeholder="Respuesta / cuerpo" />
            <button type="button" onClick={() => set({ kps: kps.filter((_, x) => x !== ki) })} style={{ ...S.btnDanger, padding: '6px 10px' }}>
              <X size={12} />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => set({ kps: [...kps, { t: '', b: '' }] })} style={S.btnSecondary}>
          <Plus size={12} /> <span>Agregar punto clave</span>
        </button>
      </div>

      {/* Quiz interactivo */}
      <div>
        <label  style={{  ...S.label }} className="adm-label">Autoevaluación rápida (opciones una por línea, correcta = índice A=0)</label>
        {quizQuestions.map((q, qi) => (
          <div key={qi} style={{ border: '1px solid rgba(245,158,11,0.25)', borderRadius: '10px', padding: '12px', marginBottom: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                id={`${uid}-quiz-q-${qi}`}
                aria-label={`Pregunta ${qi + 1}`}
                type="text" value={q.question} onChange={(e) => updateQuizQuestion(qi, { question: e.target.value })} style={S.input} placeholder="Pregunta..." />
              <button type="button" onClick={() => set({ quizQuestions: quizQuestions.filter((_, x) => x !== qi) })} style={{ ...S.btnDanger, padding: '6px 10px' }}>
                <X size={12} />
              </button>
            </div>
            <textarea
              id={`${uid}-quiz-opts-${qi}`}
              aria-label={`Opciones de la pregunta ${qi + 1} (una por línea)`}
              rows={2}
              value={(q.options || []).join('\n')}
              onChange={(e) => updateQuizQuestion(qi, { optionsText: e.target.value })}
              style={{ ...S.input, resize: 'vertical' }}
              placeholder={'Opción A\nOpción B\nOpción C'}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label  style={{  ...S.label,  margin: 0  }} className="adm-label" htmlFor={`${uid}-quiz-correct-${qi}`}>Correcta:</label>
              <input
                id={`${uid}-quiz-correct-${qi}`}
                type="number" min={0}
                value={q.correct_index}
                onChange={(e) => updateQuizQuestion(qi, { correct_index: e.target.value })}
                style={{ ...S.input, width: '80px' }}
              />
              <span style={{ fontSize: '0.68rem', color: '#64748b' }}>Índice de la opción correcta (A=0, B=1...)</span>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => set({ quizQuestions: [...quizQuestions, { question: '', options: [], correct_index: 0 }] })} style={{ ...S.btnSecondary, borderColor: 'rgba(245,158,11,0.4)', color: '#f59e0b' }}>
          <Plus size={12} /> <span>Agregar pregunta</span>
        </button>
      </div>

      {/* Flashcards de la lección */}
      <FlashcardsEditor flashcards={flashcards} onChange={(next) => set({ flashcards: next })} />

      {/* Toggles */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
          <input type="checkbox" checked={!!lesson.interactive} onChange={(e) => set({ interactive: e.target.checked })} />
          Tiene autoevaluación rápida
        </label>
        <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
          <input type="checkbox" checked={!!lesson.isQuiz} onChange={(e) => set({ isQuiz: e.target.checked })} />
          Es evaluación de módulo
        </label>
      </div>

      {/* Selector de objetos R2 (Cloudflare) */}
      {picker && (
        <R2ObjectPicker
          onPick={(url) => {
            const target = picker.target;
            if (target === 'video') set({ video_url: url });
            else if (target === 'audio') set({ audio_url: url });
            else if (target === 'infographic') set({ infographic_url: url });
            else if (target === 'slides') set({ slides_url: url });
            else if (target.startsWith('image:')) {
              const idx = parseInt(target.split(':')[1], 10);
              const list = [...images];
              if (list[idx]) list[idx] = { ...list[idx], url };
              set({ images: list });
            } else if (target.startsWith('pdf:')) {
              const idx = parseInt(target.split(':')[1], 10);
              const list = [...pdfResources];
              if (list[idx]) list[idx] = { ...list[idx], url };
              set({ pdf_resources: list });
            }
            setPicker(null);
          }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

/* ── Panel admin: páginas de lectura de Vortex Academy (Firestore) ── */
export const AdminLectorTemas = () => {
  const { user } = useAuth();

  const [structure, setStructure] = useState({ jerarquias: [], cuerpos: [], especialidadesByCuerpo: {} });
  const [structureLoading, setStructureLoading] = useState(true);
  const [fsAdmin, setFsAdmin] = useState(null); // null = sin verificar

  const [selJerarquia, setSelJerarquia] = useState('');
  const [selCuerpo, setSelCuerpo] = useState('');
  const [selEspecialidad, setSelEspecialidad] = useState('');

  // Modo de contenido: '' = conocimientos específicos por especialidad
  // (vortexAcademy/{jerarquia}/.../temas) o un areaKey general
  // (vortexAcademyAreas/{areaKey}/temas).
  const [selArea, setSelArea] = useState('');
  const [areaTemas, setAreaTemas] = useState([]);
  const [areaTemasLoading, setAreaTemasLoading] = useState(false);

  const [temas, setTemas] = useState([]);
  const [temasLoading, setTemasLoading] = useState(false);
  const [editor, setEditor] = useState(null); // { temaId, isNew, newTemaId, draft }
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  // Importación desde RTDB
  const [importing, setImporting] = useState(false);
  const [importProg, setImportProg] = useState(null); // { temasTotal, temasImportados, quizzesImportados, jerarquiaName }

  // Generación IA (individual por página/tema)
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFuente, setAiFuente] = useState('cuestionarios');
  const [aiTexto, setAiTexto] = useState('');
  const [aiFile, setAiFile] = useState(null);
  const [aiFileName, setAiFileName] = useState('');
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiNumLessons, setAiNumLessons] = useState(null); // null = IA decide automáticamente
  const [aiModel, setAiModel] = useState('DeepSeek'); // Se ajusta al cargar modelos desde Firebase
  const [aiGenerando, setAiGenerando] = useState(false);
  const [aiStep, setAiStep] = useState('');
  const aiFileInputRef = useRef(null);

  // ── Generación Masiva con IA de Temas y Lecciones ──
  const [bulkAiModalOpen, setBulkAiModalOpen] = useState(false);
  const [bulkAiFile, setBulkAiFile] = useState(null);
  const [bulkAiFileName, setBulkAiFileName] = useState('');
  const [bulkAiExtracting, setBulkAiExtracting] = useState(false);
  const [bulkAiText, setBulkAiText] = useState('');
  const [bulkAiNumTemas, setBulkAiNumTemas] = useState(3);
  const [bulkAiLessonsPerTema, setBulkAiLessonsPerTema] = useState(3);
  const [bulkAiArea, setBulkAiArea] = useState('pensamiento_matematico');
  const [bulkAiGrados, setBulkAiGrados] = useState([]);
  const [bulkAiAvailableModels, setBulkAiAvailableModels] = useState({});
  const [bulkAiSelectedModel, setBulkAiSelectedModel] = useState('DeepSeek'); // Se ajusta al cargar modelos
  const [bulkAiGenerando, setBulkAiGenerando] = useState(false);
  const [bulkAiStep, setBulkAiStep] = useState('');
  const [bulkAiProgress, setBulkAiProgress] = useState(0);
  const [bulkAiGeneratedTemas, setBulkAiGeneratedTemas] = useState([]);
  const [bulkAiPreviewTemaIndex, setBulkAiPreviewTemaIndex] = useState(0);
  const [bulkAiSaving, setBulkAiSaving] = useState(false);
  const bulkFileInputRef = useRef(null);

  // Generación audiovisual (NotebookLM en tu PC, solo admin)
  const [backendHealth, setBackendHealth] = useState(null); // null | {can_start, today_count, daily_limit}
  const [mediaTask, setMediaTask] = useState(null); // {temaId, taskId, state, progress, result, error}
  const mediaPollRef = useRef(null);

  // Sesión de NotebookLM (cuenta de Google del admin)
  const [nbStatus, setNbStatus] = useState(null); // null | {authenticated, account}
  const [nbBusy, setNbBusy] = useState(false);    // login/logout en curso
  const [nbPolling, setNbPolling] = useState(false); // vigilando el login en la ventana de Google
  const nbDeadlineRef = useRef(0);

  // Logs en vivo del backend (consola de generación audiovisual)
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]); // [{ts, level, msg}]
  const logsRef = useRef(null);

  // "Abrir backend": lanza PowerShell con start_backend.ps1 vía el protocolo
  // vortex-backend:// (registrado por backend_notebooklm/register_protocol.ps1).
  const [backendHelp, setBackendHelp] = useState(false);

  // Modal de generación audiovisual por unidad (tema / módulo / lección)
  const [mediaModal, setMediaModal] = useState(null); // { tema } | null
  const [mediaScope, setMediaScope] = useState('topic'); // topic | module | lesson
  const [mediaUnit, setMediaUnit] = useState('');
  const [mediaIncludeVideo, setMediaIncludeVideo] = useState(true); // añade video explicativo (MP4) por defecto

  // Estructura de la academia desde app_metadata (un solo get, nodo pequeño)
  useEffect(() => {
    let cancelled = false;
    getAcademyStructure()
      .then((s) => { if (!cancelled) setStructure(s); })
      .catch((e) => { if (!cancelled) setError('Error cargando la estructura Vortex Academy: ' + e.message); })
      .finally(() => { if (!cancelled) setStructureLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Estado admin Firestore (permite avisar si faltan permisos de escritura)
  useEffect(() => {
    if (!user?.uid) return undefined;
    let cancelled = false;
    getFirestoreAdminStatus(user.uid)
      .then((ok) => { if (!cancelled) setFsAdmin(ok); })
      .catch(() => { if (!cancelled) setFsAdmin(false); });
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Estado del backend NotebookLM (para saber si se puede generar hoy).
  // Se consulta al montar y se refresca tras login/logout (la cuota es por
  // cuenta de Google: cambiar de cuenta = cuota fresca).
  const refreshHealth = async () => {
    try {
      setBackendHealth(await getGeneratorHealth());
    } catch (_) {
      setBackendHealth(null);
    }
  };
  useEffect(() => {
    let cancelled = false;
    getGeneratorHealth()
      .then((h) => { if (!cancelled) setBackendHealth(h); })
      .catch(() => { if (!cancelled) setBackendHealth(null); });
    return () => { cancelled = true; };
  }, []);

  // Cuenta de Google activa en NotebookLM
  const refreshNbStatus = async () => {
    try {
      setNbStatus(await getNotebooklmStatus());
    } catch (_) {
      setNbStatus(null);
    }
  };
  useEffect(() => {
    let cancelled = false;
    getNotebooklmStatus()
      .then((s) => { if (!cancelled) setNbStatus(s); })
      .catch(() => { if (!cancelled) setNbStatus(null); });
    return () => { cancelled = true; };
  }, []);

  // Tras pulsar "Iniciar sesión" se vigila hasta que la cuenta aparezca
  // autenticada (el admin completa el login en la ventana de Google).
  useEffect(() => {
    if (!nbPolling) return undefined;
    nbDeadlineRef.current = Date.now() + 300000; // máx 5 min esperando
    const iv = setInterval(async () => {
      try {
        const s = await getNotebooklmStatus();
        setNbStatus(s);
        if (s?.authenticated || Date.now() > nbDeadlineRef.current) {
          setNbPolling(false);
          setNbBusy(false);
          // La cuenta cambió: la cuota se reinicia (es por cuenta). Refresca
          // el badge de límite diario para que deje de mostrar "alcanzado".
          refreshHealth();
        }
      } catch (_) { /* sigue esperando */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [nbPolling]);

  const handleNbLogin = async () => {
    if (nbBusy) return;
    setNbBusy(true);
    try {
      await notebooklmLogin(); // abre la ventana de Google en tu PC
      setNbPolling(true);
    } catch (e) {
      setError('No se pudo abrir el login de NotebookLM: ' + e.message);
      setNbBusy(false);
    }
  };

  const handleNbLogout = async () => {
    if (nbBusy) return;
    setNbBusy(true);
    try {
      await notebooklmLogout();
      await refreshNbStatus();
      await refreshHealth();
    } catch (e) {
      setError('No se pudo cerrar sesión de NotebookLM: ' + e.message);
    } finally {
      setNbBusy(false);
    }
  };

  const handleResetQuota = async () => {
    if (nbBusy) return;
    setNbBusy(true);
    try {
      const r = await resetGeneratorQuota();
      await refreshHealth();
      setStatus(`🔄 Cuota reiniciada para la cuenta «${r.account || 'activa'}».`);
    } catch (e) {
      setError('No se pudo reiniciar la cuota: ' + e.message);
    } finally {
      setNbBusy(false);
    }
  };

  // Polling de logs del backend mientras el panel está abierto.
  useEffect(() => {
    if (!showLogs) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { logs: items } = await getNotebooklmLogs(300);
        if (!cancelled) setLogs(items || []);
      } catch (_) { /* backend desconectado */ }
    };
    load();
    const iv = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [showLogs]);

  // Auto-scroll al final (última línea) cada vez que llegan logs nuevos.
  useEffect(() => {
    const el = logsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [logs]);

  // Limpiar polling al desmontar
  useEffect(() => () => clearInterval(mediaPollRef.current), []);

  // Páginas de lectura de la especialidad seleccionada (Firestore, tiempo real)
  useEffect(() => {
    if (!selJerarquia || !selCuerpo || !selEspecialidad) {
      setTemas([]);
      setTemasLoading(false);
      return undefined;
    }
    setTemasLoading(true);
    const unsub = watchTemas(
      { jerarquia: selJerarquia, cuerpo: selCuerpo, especialidad: selEspecialidad },
      (list) => { setTemas(list); setTemasLoading(false); }
    );
    return unsub;
  }, [selJerarquia, selCuerpo, selEspecialidad]);

  // Temas del área general seleccionada (Firestore, tiempo real)
  useEffect(() => {
    if (!selArea) {
      setAreaTemas([]);
      setAreaTemasLoading(false);
      return undefined;
    }
    setAreaTemasLoading(true);
    const unsub = watchAreaTemas(selArea, (list) => { setAreaTemas(list); setAreaTemasLoading(false); });
    return unsub;
  }, [selArea]);

  const jerarquias = structure.jerarquias;
  const cuerpos = structure.cuerpos;
  const especialidades = selCuerpo ? structure.especialidadesByCuerpo[selCuerpo] || [] : [];

  const refreshSelection = (j, c, e) => {
    setSelJerarquia(j || '');
    setSelCuerpo(c || '');
    setSelEspecialidad(e || '');
  };

  const handleSaveEditor = async () => {
    if (!editor) return;
    let temaId = editor.temaId;
    if (editor.isNew) {
      const id = (editor.newTemaId || '').trim();
      if (!id) return setError('Indica un identificador para la página (temaId).');
      temaId = id;
    }
    setBusy(true);
    setError('');
    try {
      if (selArea) {
        await saveAreaTema({ areaKey: selArea, temaId }, editor.draft);
      } else {
        await saveTema(
          { jerarquia: selJerarquia, cuerpo: selCuerpo, especialidad: selEspecialidad, temaId },
          editor.draft
        );
      }
      setStatus(`Página "${editor.draft.title || temaId}" guardada en Firestore.`);
      setEditor(null);
    } catch (err) {
      const msg = err.code === 'permission-denied'
        ? 'No tienes permisos de administrador en Firestore. Agrega tu UID en la colección admins (ver firestore.rules).'
        : err.message;
      setError('Error guardando: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (tema) => {
    if (!window.confirm(`¿Eliminar la página de lectura de "${tema.title}"?`)) return;
    setBusy(true);
    setError('');
    try {
      if (selArea) {
        await deleteAreaTema({ areaKey: selArea, temaId: tema.id });
      } else {
        await deleteTema({ jerarquia: selJerarquia, cuerpo: selCuerpo, especialidad: selEspecialidad, temaId: tema.id });
      }
      setStatus('Página eliminada.');
    } catch (err) {
      const msg = err.code === 'permission-denied'
        ? 'No tienes permisos de administrador en Firestore.'
        : err.message;
      setError('Error eliminando: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  const handleImportAll = async () => {
    if (!window.confirm(
      '¿Importar TODAS las especialidades, temas y cuestionarios desde RTDB (especialidades_temas) a Firestore?\n\n' +
      'Es una operación de una sola vez y puede tardar varios minutos. Los cuestionarios se usarán para exámenes y flashcards.'
    )) return;
    setImporting(true);
    setImportProg({ temasTotal: 0, temasImportados: 0, quizzesImportados: 0 });
    setError('');
    setStatus('');
    try {
      const res = await importAllFromRTDB({ onProgress: (p) => setImportProg(p) });
      const errMsg = res.errores.length ? ` (${res.errores.length} errores)` : '';
      setStatus(`Importación completada: ${res.temasImportados} temas y ${res.quizzesImportados} cuestionarios en Firestore${errMsg}.`);
    } catch (err) {
      setError('Error en la importación: ' + err.message);
    } finally {
      setImporting(false);
      setImportProg(null);
    }
  };

  const handleImportAreas = async () => {
    if (!window.confirm(
      '¿Importar las áreas generales (Conocimientos Marineros, Legislación Naval y Específicos generales) desde RTDB a Firestore?\n\n' +
      'Se leen los nodos "temas" (metadatos con area_id y grados por jerarquía) y "preguntas_por_tema" (banco de preguntas de los temas 1 al 25).\n' +
      'Las preguntas se reparten en exámenes de hasta 20 preguntas por tema. Puede tardar varios minutos.'
    )) return;
    setImporting(true);
    setImportProg({ temasTotal: 0, temasImportados: 0, quizzesImportados: 0 });
    setError('');
    setStatus('');
    try {
      const res = await importAreasFromRTDB({ onProgress: (p) => setImportProg(p) });
      const errMsg = res.errores.length ? ` (${res.errores.length} errores)` : '';
      setStatus(`Áreas importadas: ${res.temasImportados} temas y ${res.quizzesImportados} cuestionarios en Firestore${errMsg}.`);
    } catch (err) {
      setError('Error en la importación de áreas: ' + err.message);
    } finally {
      setImporting(false);
      setImportProg(null);
    }
  };

  const handleIndividualFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAiFile(file);
    setAiFileName(file.name);
    setAiExtracting(true);
    setError('');
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let text = '';
      if (ext === 'pdf') {
        text = await pdfToText(file);
      } else if (ext === 'docx') {
        const buf = await file.arrayBuffer();
        const res = await mammoth.extractRawText({ arrayBuffer: buf });
        text = res.value;
      } else if (ext === 'txt') {
        text = await file.text();
      } else {
        throw new Error('Formato no soportado. Usa PDF, DOCX o TXT.');
      }
      if (!text || !text.trim()) throw new Error('No se pudo extraer texto del archivo.');
      setAiTexto(text.replace(/\n\s*\n/g, '\n\n').trim());
    } catch (err) {
      setError('Error leyendo archivo: ' + err.message);
    } finally {
      setAiExtracting(false);
    }
  };

  const handleGenerateIA = async () => {
    if (!editor) return;
    const temaId = (editor.temaId || editor.newTemaId || '').trim();
    if (!temaId) return setError('Primero define el identificador (temaId) de la página.');
    setAiGenerando(true);
    setAiStep('Conectando con el modelo de IA...');
    setError('');
    setStatus('');
    try {
      const page = selArea === 'ingles'
        ? await generateInglesPaginaConIA({
            temaId,
            temaTitle: editor.draft.title || temaId,
            fuente: aiFuente === 'archivo' ? 'texto' : aiFuente,
            textoManual: aiTexto,
            userId: user?.uid,
            numLessons: aiNumLessons,
            targetModelKey: aiModel,
            onProgress: (step) => setAiStep(step),
          })
        : selArea
          ? await generateAreaPaginaConIA({
              areaKey: selArea,
              temaId,
              temaTitle: editor.draft.title || temaId,
              fuente: aiFuente === 'archivo' ? 'texto' : aiFuente,
              textoManual: aiTexto,
              userId: user?.uid,
              numLessons: aiNumLessons,
              targetModelKey: aiModel,
              onProgress: (step) => setAiStep(step),
            })
          : await generatePaginaConIA({
              jerarquia: selJerarquia,
              cuerpo: selCuerpo,
              especialidad: selEspecialidad,
              temaId,
              temaTitle: editor.draft.title || temaId,
              fuente: aiFuente === 'archivo' ? 'texto' : aiFuente,
              textoManual: aiTexto,
              userId: user?.uid,
              numLessons: aiNumLessons,
              targetModelKey: aiModel,
              onProgress: (step) => setAiStep(step),
            });

      setEditor((prev) => ({
        ...prev,
        draft: {
          ...prev.draft,
          title: prev.draft.title || page.title || temaId,
          module: prev.draft.module || page.module || '',
          lessons: page.lessons && page.lessons.length ? page.lessons : [emptyLesson()],
        },
      }));
      setAiOpen(false);
      setStatus(`✨ ¡Éxito! Se generaron ${page.lessons.length} lecciones con la IA para "${editor.draft.title || temaId}". Revisa el contenido a continuación y pulsa "Guardar página" para confirmar.`);
    } catch (err) {
      setError('Error en IA: ' + err.message);
    } finally {
      setAiGenerando(false);
      setAiStep('');
    }
  };

  // ── Handlers de Generación Masiva con IA ──
  const openBulkAiModal = async () => {
    setBulkAiModalOpen(true);
    setBulkAiStep('');
    setBulkAiProgress(0);
    setBulkAiGeneratedTemas([]);
    setBulkAiPreviewTemaIndex(0);
    if (selArea) setBulkAiArea(selArea);
    if (jerarquias && jerarquias.length) setBulkAiGrados(jerarquias);

    try {
      await vortexDocsService.initializeService();
      const models = vortexDocsService.getAvailableModels();
      const cur = vortexDocsService.getCurrentModel();
      setBulkAiAvailableModels(models || {});
      // Seleccionar modelo por defecto: priorizar DeepSeek
      const defaultKey = cur?.key || (models['DeepSeek'] ? 'DeepSeek' : (models['vertex_ai'] ? 'vertex_ai' : Object.keys(models || {})[0]));
      if (defaultKey) {
        setBulkAiSelectedModel(defaultKey);
        setAiModel(defaultKey);
      }
    } catch (e) {
      console.warn('[AdminLectorTemas] Error cargando modelos de IA:', e);
    }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkAiFile(file);
    setBulkAiFileName(file.name);
    setBulkAiExtracting(true);
    setError('');
    try {
      const ext = file.name.split('.').pop().toLowerCase();
      let text = '';
      if (ext === 'pdf') {
        text = await pdfToText(file);
      } else if (ext === 'docx') {
        const buf = await file.arrayBuffer();
        const res = await mammoth.extractRawText({ arrayBuffer: buf });
        text = res.value;
      } else if (ext === 'txt') {
        text = await file.text();
      } else {
        throw new Error('Formato no soportado. Sube un archivo PDF, DOCX o TXT.');
      }
      if (!text || !text.trim()) throw new Error('No se pudo extraer texto del archivo seleccionado.');
      setBulkAiText(text.replace(/\n\s*\n/g, '\n\n').trim());
    } catch (err) {
      setError('Error extrayendo texto del documento: ' + err.message);
    } finally {
      setBulkAiExtracting(false);
    }
  };

  const handleStartBulkGeneration = async () => {
    if (!bulkAiText || bulkAiText.trim().length < 50) {
      return setError('Pega o sube un documento con al menos 50 caracteres para generar los temas y lecciones.');
    }
    setBulkAiGenerando(true);
    setBulkAiStep('Iniciando proceso...');
    setBulkAiProgress(5);
    setError('');
    try {
      const temasGenerados = await generateTemasYLeccionesConIA({
        documentoTexto: bulkAiText,
        numTemas: bulkAiNumTemas,
        leccionesPorTema: bulkAiLessonsPerTema,
        areaKey: bulkAiArea,
        grados: bulkAiGrados,
        userId: user?.uid,
        targetModelKey: bulkAiSelectedModel,
        onProgress: ({ step, progress }) => {
          setBulkAiStep(step);
          setBulkAiProgress(progress);
        },
      });
      setBulkAiGeneratedTemas(temasGenerados);
      setBulkAiPreviewTemaIndex(0);
      setStatus(`✨ Se generaron exitosamente ${temasGenerados.length} temas con sus lecciones. Revisa la vista previa antes de guardar.`);
    } catch (err) {
      setError('Error durante la generación con IA: ' + err.message);
    } finally {
      setBulkAiGenerando(false);
    }
  };

  const handleSaveBulkToFirestore = async () => {
    if (!bulkAiGeneratedTemas || !bulkAiGeneratedTemas.length) return;
    setBulkAiSaving(true);
    setError('');
    try {
      const res = await saveGeneratedTemasToFirestore({
        areaKey: bulkAiArea,
        temasList: bulkAiGeneratedTemas,
        onProgress: ({ step }) => {
          setBulkAiStep(step);
        },
      });

      if (res.errores.length) {
        setError(`Se guardaron ${res.guardados} temas con ${res.errores.length} errores:\n` + res.errores.join('\n'));
      } else {
        setStatus(`🎉 ¡Éxito! Se guardaron ${res.guardados} temas con todas sus lecciones en Firestore (vortexAcademyAreas/${bulkAiArea}/temas).`);
        setBulkAiModalOpen(false);
        setBulkAiGeneratedTemas([]);
        setBulkAiText('');
        setBulkAiFile(null);
        setBulkAiFileName('');
        // Seleccionar el área generada para que el admin la vea de inmediato
        setSelArea(bulkAiArea);
      }
    } catch (err) {
      setError('Error al guardar temas en Firestore: ' + err.message);
    } finally {
      setBulkAiSaving(false);
    }
  };


  // ── Generación audiovisual con NotebookLM (backend local, tu cuenta de Google) ──
  const slugUnit = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unidad';

  const persistMultimedia = async (tema, result, { scope = 'topic', unit = '', unitTitle = '' } = {}) => {
    const map = { audio: 'audio_url', infographic: 'infographic_url', slides: 'slides_url', report: 'report_url', video: 'video_url' };
    const item = { title: unitTitle || '', generatedAt: Date.now() };
    Object.entries(result || {}).forEach(([kind, info]) => {
      const key = map[kind];
      if (key && info && info.url) item[key] = info.url;
    });
    // Por tema → un único set "tema". Por módulo/lección → se acumulan sets
    // por unidad (para que cada módulo/lección tenga el suyo sin pisarse).
    const prev = (tema.multimedia && tema.multimedia.items && typeof tema.multimedia.items === 'object')
      ? tema.multimedia.items
      : {};
    const items = scope === 'topic' ? { tema: item } : { ...prev, [slugUnit(unit)]: item };
    const multimedia = { generatedAt: Date.now(), scope, items };

    // Guardado automático: escribir las URLs en los campos de las lecciones
    // correspondientes (audio_url, video_url, infographic_url, slides_url) para
    // que el lector las muestre sin pegar URLs a mano. Según el alcance:
    //   topic → todas las lecciones | module → las de ese módulo | lesson → solo esa.
    const lessons = (Array.isArray(tema.lessons) ? tema.lessons : []).map((l) => {
      const match =
        scope === 'topic' ? true :
        scope === 'module' ? (l.module || '').trim() === unit :
        scope === 'lesson' ? l.id === unit || (l.title || '') === unit : false;
      if (!match) return l;
      const upd = { ...l };
      Object.entries(result || {}).forEach(([kind, info]) => {
        const key = map[kind];
        if (key && info && info.url) upd[key] = info.url;
      });
      return upd;
    });

    const updated = { ...tema, multimedia, lessons };
    if (selArea) {
      await saveAreaTema({ areaKey: selArea, temaId: tema.id }, updated);
    } else {
      await saveTema(
        { jerarquia: selJerarquia, cuerpo: selCuerpo, especialidad: selEspecialidad, temaId: tema.id },
        updated
      );
    }
  };

  const pollMediaTask = (tema, taskId, mediaOpts) => {
    clearInterval(mediaPollRef.current);
    mediaPollRef.current = setInterval(async () => {
      try {
        const t = await getTaskStatus(taskId);
        setMediaTask((prev) => ({ ...prev, ...t }));
        if (t.state === 'done') {
          clearInterval(mediaPollRef.current);
          mediaPollRef.current = null;
          await persistMultimedia(tema, t.result, mediaOpts);
          setStatus('🎙️ Material audiovisual generado y guardado en el tema.');
          setTimeout(() => {
            setMediaTask((prev) => (prev && prev.taskId === taskId ? null : prev));
          }, 4000);
        } else if (t.state === 'error') {
          clearInterval(mediaPollRef.current);
          mediaPollRef.current = null;
          setError('NotebookLM: ' + (t.error || 'Error de generación.'));
          setTimeout(() => {
            setMediaTask((prev) => (prev && prev.taskId === taskId ? null : prev));
          }, 4000);
        }
      } catch (err) {
        clearInterval(mediaPollRef.current);
        mediaPollRef.current = null;
        setError('NotebookLM: ' + err.message);
      }
    }, 6000);
  };

  const handleGenerateMedia = async (tema, { scope = 'topic', unit = '', unitTitle = '', artifacts = ['audio', 'infographic', 'slides'] } = {}) => {
    setError('');
    setStatus('');
    try {
      const area = selArea || [selJerarquia, selCuerpo, selEspecialidad].filter(Boolean).join('--');
      const payload = buildUnitPayload(tema, { scope, unit, area });
      if (!payload.lessons.length) {
        setError('NotebookLM: la unidad seleccionada no tiene lecciones con contenido.');
        return;
      }
      const { task_id } = await startGeneration(payload, artifacts);
      const mediaOpts = { scope, unit, unitTitle };
      setMediaTask({ temaId: tema.id, taskId: task_id, state: 'queued', progress: 'En cola...', result: null, error: null, scope, unit, unitTitle, artifacts, startedAt: Date.now() });
      setShowLogs(true); // abre la consola en vivo para seguir el avance
      pollMediaTask(tema, task_id, mediaOpts);
    } catch (err) {
      setError('NotebookLM: ' + err.message);
    }
  };

  const openMediaModal = (tema) => {
    setMediaScope('topic');
    setMediaUnit('');
    setMediaIncludeVideo(true);
    setMediaModal({ tema });
  };

  const submitMediaModal = async () => {
    const tema = mediaModal?.tema;
    if (!tema) return;
    if (mediaScope !== 'topic' && !mediaUnit) {
      setError('Selecciona el módulo o la lección a generar.');
      return;
    }
    let scope = mediaScope;
    let unit = mediaUnit;
    let unitTitle = mediaUnit;
    if (mediaScope === 'lesson') {
      const units = getTemaUnits(tema);
      const lesson = units.lessons.find((l) => l.id === mediaUnit);
      unitTitle = lesson ? lesson.title : mediaUnit;
    }
    const artifacts = ['audio', 'infographic', 'slides', ...(mediaIncludeVideo ? ['video'] : [])];
    setMediaModal(null);
    await handleGenerateMedia(tema, { scope, unit, unitTitle, artifacts });
  };

  const mediaTaskFor = (temaId) => (mediaTask && mediaTask.temaId === temaId ? mediaTask : null);

  const mediaTaskLabel = (task) => {
    if (!task) return '✨ Audiovisual';
    if (task.state === 'running') return 'Generando…';
    if (task.state === 'done') return '✔ Completado';
    if (task.state === 'error') return '✗ Error';
    return 'En cola…';
  };

  const mediaTaskDone = (task) => task && (task.state === 'done' || task.state === 'error');

  const fmtElapsed = (startedAt) => {
    if (!startedAt) return '';
    const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
  };

  const colorLog = (level) => {
    const l = (level || '').toUpperCase();
    if (l === 'ERROR') return '#f87171';
    if (l === 'WARNING') return '#fbbf24';
    if (l === 'DEBUG') return '#64748b';
    return '#cbd5e1';
  };

  // Abre una ventana nueva de PowerShell que arranca el backend (start_backend.ps1).
  // Usa un iframe oculto para disparar el protocolo registrado sin navegar la app.
  const openBackendTerminal = () => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = 'vortex-backend://start';
    document.body.appendChild(iframe);
    setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 4000);
    setBackendHelp(false);
  };

  const openEditor = (tema) => {
    setEditor({
      isNew: false,
      temaId: tema.id,
      draft: {
        title: tema.title,
        module: tema.module,
        grados: Array.isArray(tema.grados) ? tema.grados : [],
        lessons: tema.lessons.length ? tema.lessons : [emptyLesson()],
        multimedia: tema.multimedia || null,
      },
    });
  };

  const openNewEditor = () => {
    setEditor({
      isNew: true,
      newTemaId: '',
      temaId: '',
      draft: { title: '', module: '', grados: [], lessons: [emptyLesson()] },
    });
  };

  const updateDraftLesson = (i, lesson) => {
    setEditor((prev) => {
      const lessons = [...prev.draft.lessons];
      lessons[i] = lesson;
      return { ...prev, draft: { ...prev.draft, lessons } };
    });
  };

  const addDraftLesson = () => {
    setEditor((prev) => ({ ...prev, draft: { ...prev.draft, lessons: [...prev.draft.lessons, emptyLesson()] } }));
  };

  const removeDraftLesson = (i) => {
    setEditor((prev) => ({ ...prev, draft: { ...prev.draft, lessons: prev.draft.lessons.filter((_, x) => x !== i) } }));
  };

  const moveLesson = (i, dir) => {
    setEditor((prev) => {
      const lessons = [...prev.draft.lessons];
      const j = i + dir;
      if (j < 0 || j >= lessons.length) return prev;
      [lessons[i], lessons[j]] = [lessons[j], lessons[i]];
      return { ...prev, draft: { ...prev.draft, lessons } };
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem' }}>
      {/* Encabezado */}
      <div style={{ ...S.card, padding: '16px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <div style={{ padding: '9px', borderRadius: '9px', background: 'rgba(197,160,89,0.12)', color: '#c5a059', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BookOpen size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 2 }}>
          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>
            Lector de Temas
          </p>
          <span style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
            {selArea
              ? `Firestore: vortexAcademyAreas/${selArea}/temas`
              : `Firestore: vortexAcademy/{jerarquía}/cuerpos/{cuerpo}/especialidades/{especialidad}/temas`}
          </span>
        </div>
        {!structureLoading && <span style={S.badge('#c5a059')}>{jerarquias.length} jerarquías</span>}
        <span style={S.badge('#34d399')}>{selArea ? areaTemas.length : temas.length} páginas</span>
        {(() => {
          const authedNow = !!nbStatus?.authenticated;
          const text = !backendHealth
            ? '🎙️ backend desconectado'
            : !authedNow
              ? '🎙️ sin cuenta de Google'
              : backendHealth.can_start
                ? `🎙️ ${backendHealth.today_count}/${backendHealth.daily_limit} hoy`
                : backendHealth.reason && backendHealth.reason.includes('cooldown')
                  ? `🎙️ ${backendHealth.reason}${backendHealth.account ? ` (${backendHealth.account})` : ''}`
                  : '🎙️ límite diario alcanzado';
          const color = !backendHealth ? '#64748b' : authedNow && backendHealth.can_start ? '#34d399' : '#f59e0b';
          return <span title="Backend de generación audiovisual (NotebookLM) en tu PC" style={S.badge(color)}>{text}</span>;
        })()}
        {(() => {
          const authed = !!nbStatus?.authenticated;
          const acct = nbStatus?.account || '';
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <span
                title="Cuenta de Google conectada a NotebookLM (la que genera el audiovisual)"
                style={S.badge(authed ? '#34d399' : '#f87171')}
              >
                {(nbBusy || nbPolling) ? (
                  <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                ) : authed ? (
                  <>👤 {acct}</>
                ) : (
                  <>👤 sin sesión</>
                )}
              </span>
              {authed ? (
                <>
                  <button
                    onClick={handleNbLogout}
                    disabled={nbBusy}
                    title="Cerrar sesión de NotebookLM para entrar con otra cuenta de Google"
                     style={{  ...S.btnDanger }} className="adm-btn adm-btn--secondary"
                  >
                    <LogOut size={11} />
                    <span>Cerrar sesión</span>
                  </button>
                  {backendHealth && !backendHealth.can_start && (
                    <button
                      onClick={handleResetQuota}
                      disabled={nbBusy}
                      title="Desbloquea la cuota de esta cuenta (contador a 0, sin cooldown). Úsalo si cambiaste de cuenta y aún veías el cooldown anterior."
                       style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary"
                    >
                      <RotateCcw size={11} />
                      <span>Reiniciar cuota</span>
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={handleNbLogin}
                  disabled={nbBusy}
                  title="Abrir la ventana de Google para iniciar sesión (o cambiar de cuenta)"
                   style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary"
                >
                  <LogIn size={11} />
                  <span>{nbPolling ? 'Esperando login…' : 'Iniciar sesión'}</span>
                </button>
              )}
            </span>
          );
        })()}
        <button
          onClick={() => setShowLogs((v) => !v)}
          title="Consola en vivo del backend (generación audiovisual)"
          style={{ ...S.btnSecondary, borderColor: showLogs ? 'rgba(197,160,89,0.7)' : 'rgba(197,160,89,0.3)', color: showLogs ? '#d8b978' : '#c5a059' }}
        >
          <Terminal size={14} />
          <span>{showLogs ? 'Ocultar logs' : 'Ver logs'}</span>
        </button>
        <button
          onClick={openBackendTerminal}
          title="Abre PowerShell y arranca el backend NotebookLM (necesario para generar el audiovisual)"
           style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary"
        >
          <Play size={14} />
          <span>Abrir backend</span>
        </button>
        <button
          onClick={() => setBackendHelp((v) => !v)}
          title="Ayuda para arrancar el backend (si el botón no funciona)"
          style={{ ...S.btnSecondary, padding: '8px 10px' }}
          aria-label="Ayuda para arrancar el backend"
        >
          <HelpCircle size={14} />
        </button>
        <button
          onClick={openBulkAiModal}
          disabled={busy}
          title="Genera múltiples temas y lecciones estructuradas a partir de un documento o texto usando IA"
           style={{ 
            ...S.btnPrimary }} className="adm-btn adm-btn--primary"
        >
          <Sparkles size={15} />
          <span>Generar Temas y Lecciones con IA</span>
        </button>
        <button
          onClick={handleImportAll}
          disabled={importing || busy}
           style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary"
        >
          {importing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
          <span>{importing ? 'Importando desde RTDB...' : 'Importar especialidades'}</span>
        </button>
        <button
          onClick={handleImportAreas}
          disabled={importing || busy}
           style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary"
        >
          {importing ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
          <span>{importing ? 'Importando áreas...' : 'Importar áreas generales (temas 1-25)'}</span>
        </button>
      </div>

      {/* Logs del backend NotebookLM en vivo */}
      {showLogs && (
        <div style={{ ...S.card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
            <span style={{ fontSize: '0.74rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Terminal size={13} style={{ color: '#c5a059' }} />
              Logs del backend NotebookLM
            </span>
            <span style={{ fontSize: '0.66rem', color: '#64748b' }}>actualiza cada 4 s</span>
          </div>
          <div
            ref={logsRef}
            style={{
              background: 'rgba(0,0,0,0.35)', borderRadius: '8px', padding: '10px',
              height: '260px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.68rem', lineHeight: 1.5,
            }}
          >
            {logs.length === 0 ? (
              <div style={{ color: '#64748b' }}>Sin logs todavía (¿está corriendo el backend en tu PC?).</div>
            ) : (
              logs.map((l, i) => (
                <div key={i} style={{ color: colorLog(l.level), whiteSpace: 'pre-wrap' }}>
                  <span style={{ color: '#475569' }}>[{l.ts}]</span> {l.msg}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Ayuda para arrancar el backend */}
      {backendHelp && (
        <div style={{ ...S.card, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <HelpCircle size={15} style={{ color: '#34d399' }} />
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff' }}>Arrancar el backend NotebookLM</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.6 }}>
            "Abrir backend" usa el protocolo <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>vortex-backend://</code> para abrir
            una ventana nueva de PowerShell que lanza el servidor. Si el navegador pregunta, acepta abrir la aplicación.
            Si el botón no hace nada, es que el protocolo aún no está registrado. Ejecuta <strong>una sola vez</strong> en PowerShell:
          </p>
          <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '8px', padding: '10px', fontFamily: 'monospace', fontSize: '0.68rem', color: '#a7f3d0', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {String.raw`& "C:\Users\Equipo\AndroidStudioProjects\ascensosapp\pagina_ascensos_app_vortex\backend_notebooklm\register_protocol.ps1"`}
          </div>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#64748b', lineHeight: 1.6 }}>
            Alternativa manual (copia y pega en una terminal PowerShell):
          </p>
          <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: '8px', padding: '10px', fontFamily: 'monospace', fontSize: '0.68rem', color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {String.raw`cd C:\Users\Equipo\AndroidStudioProjects\ascensosapp\pagina_ascensos_app_vortex\backend_notebooklm
.\start_backend.ps1`}
          </div>
          <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b' }}>
            El servidor debe quedar corriendo en <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>http://localhost:8000</code>.
            Recuerda reiniciarlo tras cambiar <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>NOTEBOOKLM_LANGUAGE</code> en <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>backend_notebooklm/.env</code>.
          </p>
        </div>
      )}

      {/* Progreso de importación */}
      {importing && importProg && (
        <div style={{ ...S.card, padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', fontSize: '0.76rem', color: '#94a3b8' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: '#34d399' }} />
              {selArea ? `Importando ${AREA_NAMES[selArea] || selArea} → Firestore...` : 'Importando especialidades_temas → Firestore...'}
            </span>
            <span style={S.badge('#c5a059')}>{importProg.temasImportados}/{importProg.temasTotal} temas</span>
            <span style={S.badge('#f59e0b')}>{importProg.quizzesImportados} cuestionarios</span>
            {(importProg.jerarquiaName || importProg.areaKey) && (
              <span style={{ fontFamily: 'monospace', fontSize: '0.68rem' }}>
                {importProg.areaKey ? (AREA_NAMES[importProg.areaKey] || importProg.areaKey) : importProg.jerarquiaName}
              </span>
            )}
          </div>
          <div style={{ height: '6px', borderRadius: '4px', background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '4px',
              background: 'linear-gradient(90deg, #c5a059, #8a6a2e)',
              width: importProg.temasTotal ? `${Math.min(100, Math.round((importProg.temasImportados / importProg.temasTotal) * 100))}%` : '0%',
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      {/* Aviso permisos Firestore */}
      {fsAdmin === false && (
        <div style={{ ...S.card, padding: '12px 16px', borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', fontSize: '0.76rem', color: '#fcd34d', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <ShieldAlert size={16} style={{ flexShrink: 0 }} />
          <span>
            Firestore aún no te reconoce como administrador para <strong>escribir</strong>.
            Agrega tu UID como documento en la colección <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>admins/{'{'}tuUID{'}'}</code> de Firestore.
          </span>
        </div>
      )}

      {/* Selectores */}
      <div style={{ ...S.card, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          <span style={{ ...S.label, margin: 0, display: 'inline' }}>Contenido:</span>
          <button
            type="button"
            onClick={() => { setSelArea(''); setAreaTemas([]); }}
            style={{ ...S.btnSecondary, borderColor: !selArea ? 'rgba(197,160,89,0.5)' : 'rgba(255,255,255,0.1)', color: !selArea ? '#c5a059' : '#94a3b8', background: !selArea ? 'rgba(197,160,89,0.12)' : 'rgba(255,255,255,0.03)' }}
          >
            🎯 Conocimientos Específicos (por especialidad)
          </button>
          {GENERAL_AREAS.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => { setSelArea(a.key); refreshSelection('', '', ''); }}
              style={{ ...S.btnSecondary, borderColor: selArea === a.key ? `${a.color}88` : 'rgba(255,255,255,0.1)', color: selArea === a.key ? a.color : '#94a3b8', background: selArea === a.key ? `${a.color}18` : 'rgba(255,255,255,0.03)' }}
            >
              {a.label}
            </button>
          ))}
        </div>

        {!selArea && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <div>
              <label style={S.label} htmlFor="sel-jerarquia">Jerarquía</label>
              <select id="sel-jerarquia" value={selJerarquia} onChange={(e) => refreshSelection(e.target.value, '', '')} style={S.input} disabled={structureLoading}>
                <option value="">Seleccionar...</option>
                {jerarquias.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label} htmlFor="sel-cuerpo">Cuerpo / Servicio</label>
              <select id="sel-cuerpo" value={selCuerpo} onChange={(e) => refreshSelection(selJerarquia, e.target.value, '')} style={S.input} disabled={!selJerarquia}>
                <option value="">Seleccionar...</option>
                {cuerpos.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label} htmlFor="sel-especialidad">Especialidad</label>
              <select id="sel-especialidad" value={selEspecialidad} onChange={(e) => refreshSelection(selJerarquia, selCuerpo, e.target.value)} style={S.input} disabled={!selCuerpo}>
                <option value="">Seleccionar...</option>
                {especialidades.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
        )}

        {selArea && (
          <div style={{ borderRadius: '10px', padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.76rem', color: '#94a3b8', lineHeight: 1.6 }}>
            {AREA_NAMES[selArea]} es un área <strong style={{ color: '#cbd5e1' }}>general</strong>: sus temas aplican a todos los cuerpos y especialidades. Cada tema define en{' '}
            <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 5px', borderRadius: '4px' }}>grados</code> qué jerarquías lo ven.{' '}
            Edita un tema para <strong style={{ color: '#cbd5e1' }}>asignar jerarquías</strong> (los marineros solo verán los temas que les asignes).
          </div>
        )}
      </div>

      {status && (
        <div style={{ ...S.card, padding: '12px 16px', borderColor: 'rgba(52,211,153,0.4)', background: 'rgba(16,185,129,0.08)', fontSize: '0.78rem', color: '#a7f3d0' }}>
          <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />{status}
        </div>
      )}
      {error && (
        <div style={{ ...S.card, padding: '12px 16px', borderColor: 'rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.08)', fontSize: '0.78rem', color: '#fecaca' }}>
          <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />{error}
        </div>
      )}

      {/* Lista de páginas */}
      {selArea ? (
        /* ── Modo área general ── */
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button onClick={openNewEditor} disabled={busy}  style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary">
              <FilePlus size={14} />
              <span>Nuevo tema de área</span>
            </button>
            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
              Temas de {AREA_NAMES[selArea] || selArea} en Firestore. Pulsa "Importar áreas generales" si aún no existen.
            </span>
          </div>

          {areaTemasLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#94a3b8', fontSize: '0.8rem', padding: '0.5rem 0' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Cargando temas del área...
            </div>
          )}

          {!areaTemasLoading && areaTemas.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              <Layers size={32} style={{ color: '#64748b', opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
                Aún no hay temas de esta área en Firestore. Importa las áreas generales desde RTDB (temas 1-25).
              </p>
              <button onClick={handleImportAreas} disabled={importing || busy}  style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary">
                <Download size={14} /> <span>Importar áreas generales</span>
              </button>
            </div>
          ) : (
            areaTemas.map((tema, tIdx) => (
              <div key={tema.id} style={{ ...S.card }}>
                <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', background: 'rgba(7,21,38,0.4)' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: 'rgba(192,132,252,0.12)',
                    border: '1px solid rgba(192,132,252,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 800, color: '#c084fc',
                  }}>
                    {tIdx + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tema.title}
                    </p>
                    <span style={{ fontSize: '0.66rem', color: '#64748b', fontFamily: 'monospace' }}>{tema.id}{tema.module ? ` · ${tema.module}` : ''}</span>
                  </div>
                  <span style={S.badge('#34d399')}>{tema.lessons.length} lecciones</span>
                  <span style={S.badge('#f59e0b')}>🎯 {tema.cuestionariosCount} preguntas</span>
                  <span style={{ ...S.badge('#c5a059'), maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tema.grados.length ? `Jerarquías: ${tema.grados.join(', ')}` : '⚠️ Sin jerarquías asignadas'}
                  </span>
                  <button onClick={() => openEditor(tema)} disabled={busy} style={S.btnSecondary}>
                    <Edit3 size={12} /> <span>Editar / Asignar</span>
                  </button>
                  <button
                    onClick={() => openMediaModal(tema)}
                    disabled={busy || !!mediaTaskFor(tema.id)}
                    title="Genera podcast, infografía y presentación de este tema o de un módulo/lección con tu NotebookLM"
                    style={{ ...S.btnSecondary, borderColor: 'rgba(216,185,120,0.4)', color: '#d8b978' }}
                  >
                    <Sparkles size={12} />
                    <span>{mediaTaskLabel(mediaTaskFor(tema.id))}</span>
                  </button>
                  {tema.multimedia && <span style={S.badge('#34d399')}>🎙️ OK</span>}
                  <button onClick={() => handleDelete(tema)} disabled={busy} style={{ ...S.btnDanger, padding: '8px 12px' }}>
                    <Trash2 size={12} /> <span>Eliminar</span>
                  </button>
                </div>
                {mediaTaskFor(tema.id) && (() => {
                  const task = mediaTaskFor(tema.id);
                  const isErr = task.state === 'error';
                  const isDone = task.state === 'done';
                  return (
                    <div style={{ padding: '10px 18px', borderTop: '1px solid rgba(197,160,89,0.15)', fontSize: '0.72rem', color: isErr ? '#f87171' : (isDone ? '#34d399' : '#c4b5fd'), display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {isErr ? <AlertTriangle size={12} /> : isDone ? <Check size={12} /> : <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                      <span style={{ flex: 1, minWidth: '120px' }}>{isErr ? (task.error || 'Error de generación') : (isDone ? 'Completado · URLs guardadas en el tema' : (task.progress || 'Procesando artefactos (avance en vivo)...'))}</span>
                      <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>⏱ {fmtElapsed(task.startedAt)}</span>
                    </div>
                  );
                })()}
              </div>
            ))
          )}
        </div>
      ) : !selEspecialidad ? (
        <div style={{ ...S.card, padding: '3rem', textAlign: 'center' }}>
          <Layers size={36} style={{ color: '#c5a059', opacity: 0.35 }} />
          <p style={{ margin: '12px 0 0', fontSize: '0.82rem', color: '#64748b' }}>
            Selecciona jerarquía, cuerpo/servicio y especialidad para gestionar sus páginas de lectura en Firestore.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <button onClick={openNewEditor} disabled={busy}  style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary">
              <FilePlus size={14} />
              <span>Nueva página de lectura</span>
            </button>
            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
              Crea una página en blanco y edita sus lecciones manualmente.
            </span>
          </div>

          {temasLoading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#94a3b8', fontSize: '0.8rem', padding: '0.5rem 0' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
              Cargando páginas de lectura...
            </div>
          )}

          {!temasLoading && temas.length === 0 ? (
            <div style={{ ...S.card, padding: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
              <Layers size={32} style={{ color: '#64748b', opacity: 0.4 }} />
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
                Aún no hay páginas de lectura para esta especialidad. Crea la primera desde cero.
              </p>
              <button onClick={openNewEditor} disabled={busy}  style={{  ...S.btnSecondary }} className="adm-btn adm-btn--secondary">
                <FilePlus size={14} /> <span>Crear la primera página</span>
              </button>
            </div>
          ) : (
            temas.map((tema, tIdx) => (
              <div key={tema.id} style={{ ...S.card }}>
                <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', background: 'rgba(7,21,38,0.4)' }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: 'rgba(52,211,153,0.12)',
                    border: '1px solid rgba(52,211,153,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.78rem', fontWeight: 800, color: '#34d399',
                  }}>
                    {tIdx + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tema.title}
                    </p>
                    <span style={{ fontSize: '0.66rem', color: '#64748b', fontFamily: 'monospace' }}>{tema.id}{tema.module ? ` · ${tema.module}` : ''}</span>
                  </div>
                  <span style={S.badge('#34d399')}>{tema.lessons.length} lecciones</span>
                  <span style={S.badge('#f59e0b')}>🎯 {tema.cuestionariosCount} cuestionarios</span>
                  <button onClick={() => openEditor(tema)} disabled={busy} style={S.btnSecondary}>
                    <Edit3 size={12} /> <span>Editar</span>
                  </button>
                  <button
                    onClick={() => openMediaModal(tema)}
                    disabled={busy || !!mediaTaskFor(tema.id)}
                    title="Genera podcast, infografía y presentación de este tema o de un módulo/lección con tu NotebookLM"
                    style={{ ...S.btnSecondary, borderColor: 'rgba(216,185,120,0.4)', color: '#d8b978' }}
                  >
                    <Sparkles size={12} />
                    <span>{mediaTaskLabel(mediaTaskFor(tema.id))}</span>
                  </button>
                  {tema.multimedia && <span style={S.badge('#34d399')}>🎙️ OK</span>}
                  <button onClick={() => handleDelete(tema)} disabled={busy} style={{ ...S.btnDanger, padding: '8px 12px' }}>
                    <Trash2 size={12} /> <span>Eliminar</span>
                  </button>
                </div>
                {mediaTaskFor(tema.id) && (() => {
                  const task = mediaTaskFor(tema.id);
                  const isErr = task.state === 'error';
                  const isDone = task.state === 'done';
                  return (
                    <div style={{ padding: '10px 18px', borderTop: '1px solid rgba(197,160,89,0.15)', fontSize: '0.72rem', color: isErr ? '#f87171' : (isDone ? '#34d399' : '#c4b5fd'), display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {isErr ? <AlertTriangle size={12} /> : isDone ? <Check size={12} /> : <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />}
                      <span style={{ flex: 1, minWidth: '120px' }}>{isErr ? (task.error || 'Error de generación') : (isDone ? 'Completado · URLs guardadas en el tema' : (task.progress || 'Procesando artefactos (avance en vivo)...'))}</span>
                      <span style={{ color: '#94a3b8', whiteSpace: 'nowrap' }}>⏱ {fmtElapsed(task.startedAt)}</span>
                    </div>
                  );
                })()}
              </div>
            ))
          )}
        </div>
      )}

      {/* ── MODAL EDITOR DE PÁGINA ── */}
      {editor && (
        <ModalShell
          title={`${editor.isNew ? 'Nueva página de lectura' : `Editar página · ${editor.draft.title}`}`}
          icon={<BookOpen size={16} style={{ color: '#c5a059' }} />}
          onClose={() => setEditor(null)}
          onSubmit={handleSaveEditor}
          submitLabel="Guardar página"
        >
          {editor.isNew && (
            <div>
              <label style={S.label} htmlFor="ed-tema-id">Identificador (temaId) · clave en Firestore</label>
              <input
                id="ed-tema-id"
                type="text"
                value={editor.newTemaId || ''}
                onChange={(e) => setEditor({ ...editor, newTemaId: e.target.value })}
                style={S.input}
                placeholder="tema-1-fundamentos-legales"
              />
              <span style={{ fontSize: '0.66rem', color: '#64748b' }}>
                Clave única del documento en {selArea ? `vortexAcademyAreas/${selArea}/temas` : 'vortexAcademy/{...}/temas'}. Evita espacios, acentos y diagonales.
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label} htmlFor="ed-title">Título de la página (tema)</label>
              <input
                id="ed-title"
                type="text"
                value={editor.draft.title}
                onChange={(e) => setEditor({ ...editor, draft: { ...editor.draft, title: e.target.value } })}
                style={S.input}
                placeholder="Tema 1: Fundamentos Legales"
              />
            </div>
            <div>
              <label style={S.label} htmlFor="ed-module">Módulo / etiqueta</label>
              <input
                id="ed-module"
                type="text"
                value={editor.draft.module}
                onChange={(e) => setEditor({ ...editor, draft: { ...editor.draft, module: e.target.value } })}
                style={S.input}
                placeholder="Módulo 1 · Fundamentos Legales"
              />
            </div>
          </div>

          {selArea && (
            <div style={{
              border: '1px solid rgba(197,160,89,0.3)', borderRadius: '12px', padding: '14px 16px',
              background: 'rgba(197,160,89,0.06)', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldAlert size={15} style={{ color: '#c5a059' }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0' }}>Asignación por jerarquía</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.66rem', color: '#c5a059' }}>{editor.draft.grados.length} de {jerarquias.length} jerarquías</span>
              </div>
              <p style={{ margin: 0, fontSize: '0.7rem', color: '#94a3b8', lineHeight: 1.5 }}>
                Solo las jerarquías marcadas verán este tema en su lector. Desmarca todas para ocultarlo.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {jerarquias.map((j) => {
                  const active = (editor.draft.grados || []).includes(j);
                  return (
                    <button
                      key={j}
                      type="button"
                      onClick={() => {
                        const grados = active
                          ? (editor.draft.grados || []).filter((g) => g !== j)
                          : [...(editor.draft.grados || []), j];
                        setEditor({ ...editor, draft: { ...editor.draft, grados } });
                      }}
                      style={{
                        padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.68rem', fontWeight: 700,
                        fontFamily: "'Inter', sans-serif", textTransform: 'uppercase', letterSpacing: '0.04em',
                        border: active ? '1px solid rgba(197,160,89,0.6)' : '1px solid rgba(255,255,255,0.12)',
                        background: active ? 'rgba(197,160,89,0.2)' : 'rgba(255,255,255,0.03)',
                        color: active ? '#d8b978' : '#94a3b8',
                      }}
                    >
                      {active ? '✓ ' : ''}{j}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Generación con IA */}
          <div style={{
            border: '1px dashed rgba(197,160,89,0.4)', borderRadius: '12px', padding: '14px 16px',
            background: 'rgba(197,160,89,0.06)', display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={15} style={{ color: '#d8b978' }} />
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0' }}>Generar contenido con IA</span>
              <button
                type="button"
                onClick={() => setAiOpen((v) => !v)}
                style={{ marginLeft: 'auto', ...S.btnSecondary, borderColor: 'rgba(197,160,89,0.4)', color: '#d8b978' }}
              >
                <Plus size={12} /> <span>{aiOpen ? 'Ocultar' : 'Opciones'}</span>
              </button>
            </div>
            {aiOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '0.76rem', color: '#cbd5e1' }}>
                  <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                    <input
                      type="radio"
                      name="aiFuente"
                      checked={aiFuente === 'cuestionarios'}
                      onChange={() => setAiFuente('cuestionarios')}
                    />
                    📝 Desde los cuestionarios / preguntas del tema
                  </label>
                  <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                    <input
                      type="radio"
                      name="aiFuente"
                      checked={aiFuente === 'texto'}
                      onChange={() => setAiFuente('texto')}
                    />
                    📋 Desde texto manual
                  </label>
                  <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px' }} className="adm-label">
                    <input
                      type="radio"
                      name="aiFuente"
                      checked={aiFuente === 'archivo'}
                      onChange={() => setAiFuente('archivo')}
                    />
                    📁 Subir documento (PDF / DOCX / TXT)
                  </label>
                </div>

                {/* Subir archivo para este tema */}
                {aiFuente === 'archivo' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        ref={aiFileInputRef}
                        type="file"
                        accept=".pdf,.docx,.txt"
                        onChange={handleIndividualFileSelect}
                         style={{  display: 'none'  }} className="adm-input"
                      />
                      <button
                        type="button"
                        onClick={() => aiFileInputRef.current?.click()}
                        disabled={aiExtracting || aiGenerando}
                        style={{
                          ...S.btnSecondary,
                          borderColor: 'rgba(56,189,248,0.5)',
                          color: '#38bdf8',
                          padding: '6px 12px',
                          fontSize: '0.74rem',
                        }}
                      >
                        {aiExtracting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
                        <span>{aiExtracting ? 'Extrayendo texto...' : aiFileName ? `Cambiar archivo (${aiFileName})` : 'Seleccionar PDF / DOCX / TXT'}</span>
                      </button>
                      {aiFileName && (
                        <span style={{ fontSize: '0.72rem', color: '#34d399', fontWeight: 600 }}>
                          ✓ {aiFileName} ({aiTexto.length.toLocaleString()} caracteres extraídos)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Texto manual o texto extraído */}
                {(aiFuente === 'texto' || aiFuente === 'archivo') && (
                  <textarea
                    id="ed-ai-texto"
                    aria-label="Texto para generar contenido con IA"
                    rows={5}
                    value={aiTexto}
                    onChange={(e) => setAiTexto(e.target.value)}
                    style={{ ...S.input, resize: 'vertical', fontFamily: 'monospace', fontSize: '0.75rem' }}
                    placeholder="Pega aquí el contenido, directivas o manual del tema para que la IA genere las lecciones estructuradas..."
                  />
                )}

                {/* Parámetros: número de lecciones y modelo */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: 'rgba(7,21,38,0.4)', padding: '10px 12px', borderRadius: '8px' }}>
                  <div>
                    <label style={S.label} htmlFor="ed-ai-lessons-count">Cantidad de Lecciones</label>
                    <label  style={{  display: 'flex',  alignItems: 'center',  gap: '6px',  marginBottom: '6px' }} className="adm-label">
                      <input
                        type="checkbox"
                        checked={aiNumLessons === null}
                        onChange={(e) => setAiNumLessons(e.target.checked ? null : 3)}
                      />
                      🤖 La IA decide la cantidad según el contenido
                    </label>
                    {aiNumLessons !== null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          id="ed-ai-lessons-count"
                          type="number"
                          min={1}
                          max={8}
                          value={aiNumLessons}
                          onChange={(e) => setAiNumLessons(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))}
                          style={S.input}
                        />
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>lecciones</span>
                      </div>
                    )}
                    {aiNumLessons === null && (
                      <p style={{ margin: 0, fontSize: '0.7rem', color: '#64748b', lineHeight: 1.4 }}>
                        La IA analizará el contenido y generará las lecciones necesarias (2-10) para cubrir toda la información.
                      </p>
                    )}
                  </div>

                  <div>
                    <label style={S.label} htmlFor="ed-ai-model-sel">Modelo de IA</label>
                    <select
                      id="ed-ai-model-sel"
                      value={aiModel}
                      onChange={(e) => setAiModel(e.target.value)}
                      style={S.input}
                     >
                       {/* Opciones dinámicas desde Firebase (incluye DeepSeek, Gemini, etc.) */}
                       {Object.keys(bulkAiAvailableModels).length > 0
                         ? Object.entries(bulkAiAvailableModels).map(([k, m]) => (
                             <option key={k} value={k}>
                               {m.displayName || m.modelName || k}
                             </option>
                           ))
                         : (
                             <>
                               <option value="DeepSeek">DeepSeek V4 Flash (OpenRouter) ⭐ Recomendado</option>
                               <option value="gemini-2.5-flash">Gemini 2.5 Flash (Google AI)</option>
                               <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite</option>
                             </>
                           )
                       }
                     </select>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleGenerateIA}
                    disabled={aiGenerando || busy}
                     style={{  ...S.btnPrimary }} className="adm-btn adm-btn--primary"
                  >
                    {aiGenerando ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={14} />}
                    <span>{aiGenerando ? 'Generando lecciones con IA...' : aiNumLessons === null ? '✨ Generar lecciones (la IA decide la cantidad)' : `Generar ${aiNumLessons} lecciones y rellenar página`}</span>
                  </button>
                  {aiGenerando && aiStep && (
                    <span style={{ fontSize: '0.72rem', color: '#c4b5fd', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      {aiStep}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ ...S.label, margin: 0 }}>Lecciones de la página ({editor.draft.lessons.length})</span>
              <button type="button" onClick={addDraftLesson} style={S.btnPrimary}>
                <Plus size={13} /> <span>Agregar lección</span>
              </button>
            </div>
            {editor.draft.lessons.map((lesson, i) => (
              <div key={lesson.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileQuestion size={13} style={{ color: '#f59e0b' }} />
                  <span style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lesson.title || `Lección ${i + 1}`}
                  </span>
                  <button type="button" onClick={() => moveLesson(i, -1)} disabled={i === 0} style={{ ...S.btnSecondary, padding: '5px 8px' }}>
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={() => moveLesson(i, 1)} disabled={i === editor.draft.lessons.length - 1} style={{ ...S.btnSecondary, padding: '5px 8px' }}>
                    <ChevronDown size={12} />
                  </button>
                  <button type="button" onClick={() => removeDraftLesson(i)} style={{ ...S.btnDanger, padding: '5px 8px' }}>
                    <Trash2 size={12} />
                  </button>
                </div>
                <LessonEditor lesson={lesson} onChange={(l) => updateDraftLesson(i, l)} />
              </div>
            ))}
          </div>
        </ModalShell>
      )}

      {/* Modal: generación audiovisual por unidad (tema / módulo / lección) */}
      {mediaModal && (() => {
        const units = getTemaUnits(mediaModal.tema);
        const nLessons = units.lessons.length;
        return (
          <ModalShell
            title="✨ Generar audiovisual"
            icon={<Sparkles size={18} />}
            onClose={() => setMediaModal(null)}
            onSubmit={submitMediaModal}
            submitLabel={mediaIncludeVideo ? 'Generar · audio + infografía + diapositivas + video' : 'Generar (1 de tu cuota)'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                Elige qué quieres generar. Cada set (audio + infografía + presentación + reporte) consume 1 de las
                4 generaciones del día. El contenido se genera con las lecciones de la unidad seleccionada.
              </p>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={mediaIncludeVideo}
                  onChange={(e) => setMediaIncludeVideo(e.target.checked)}
                  style={{ width: '16px', height: '16px', cursor: 'pointer', marginTop: '2px', flexShrink: 0 }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0' }}>🎬 Incluir video explicativo (MP4)</span>
                  <span style={{ fontSize: '0.66rem', color: '#64748b', fontWeight: 400, lineHeight: 1.35 }}>
                    NotebookLM genera un video-resumen en español. Tarda más (5–15 min) y usa estilo "classic".
                  </span>
                </span>
              </label>

              <label style={S.label}>Granularidad</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { value: 'topic', label: `Tema completo (${nLessons} lecciones)`, desc: 'Un solo set con todo el tema.' },
                  { value: 'module', label: `Un módulo (${units.modules.length} disponibles)`, desc: 'Solo las lecciones de un módulo (campo "módulo" de las lecciones).' },
                  { value: 'lesson', label: 'Una lección', desc: 'Solo el contenido de esa lección.' },
                ].map((opt) => {
                  const selected = mediaScope === opt.value;
                  return (
                  <label
                    key={opt.value}
                    onClick={() => { setMediaScope(opt.value); setMediaUnit(''); }}
                    style={{
                      display: 'flex', gap: '12px', alignItems: 'center', padding: '11px 14px', cursor: 'pointer',
                      borderRadius: '10px', border: `1px solid ${selected ? 'rgba(216,185,120,0.7)' : 'rgba(197,160,89,0.2)'}`,
                      background: selected ? 'rgba(216,185,120,0.08)' : 'rgba(7,21,38,0.4)',
                      transition: 'border-color .15s, background .15s',
                    }}
                  >
                    <span
                      style={{
                        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box',
                        border: `2px solid ${selected ? '#d8b978' : '#475569'}`,
                        background: selected ? 'radial-gradient(circle, #d8b978 0 4px, transparent 5px)' : 'transparent',
                      }}
                    />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#e2e8f0' }}>{opt.label}</span>
                      <span style={{ fontSize: '0.7rem', color: '#64748b', lineHeight: 1.35 }}>{opt.desc}</span>
                    </span>
                  </label>
                  );
                })}
              </div>

              {mediaScope === 'module' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={S.label} htmlFor="media-modulo">Módulo</label>
                  <select
                    id="media-modulo"
                    value={mediaUnit}
                    onChange={(e) => setMediaUnit(e.target.value)}
                    style={S.input}
                  >
                    <option value="">— Selecciona un módulo —</option>
                    {units.modules.map((m) => (
                      <option key={m.name} value={m.name}>{m.name} ({m.count} lecciones)</option>
                    ))}
                  </select>
                </div>
              )}

              {mediaScope === 'lesson' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={S.label} htmlFor="media-leccion">Lección</label>
                  <select
                    id="media-leccion"
                    value={mediaUnit}
                    onChange={(e) => setMediaUnit(e.target.value)}
                    style={S.input}
                  >
                    <option value="">— Selecciona una lección —</option>
                    {units.lessons.map((l) => (
                      <option key={l.id} value={l.id}>{l.title} · {l.module}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </ModalShell>
        );
      })()}

      {/* ── MODAL GENERADOR MASIVO DE TEMAS Y LECCIONES CON IA ── */}
      {bulkAiModalOpen && (
        <ModalShell
          title="✨ Generador de Temas y Lecciones con Inteligencia Artificial"
          icon={<Sparkles size={18} style={{ color: '#ec4899' }} />}
          onClose={() => {
            if (bulkAiGenerando || bulkAiSaving) {
              if (!window.confirm('Hay un proceso en curso. ¿Deseas cerrar el modal?')) return;
            }
            setBulkAiModalOpen(false);
          }}
          onSubmit={() => {
            if (bulkAiGeneratedTemas.length > 0) {
              handleSaveBulkToFirestore();
            } else {
              handleStartBulkGeneration();
            }
          }}
          submitLabel={
            bulkAiSaving
              ? 'Guardando en Firestore...'
              : bulkAiGeneratedTemas.length > 0
                ? `Guardar ${bulkAiGeneratedTemas.length} Temas en Firestore`
                : bulkAiGenerando
                  ? 'Generando...'
                  : 'Iniciar Generación con IA'
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {/* Descripción introductoria */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(236,72,153,0.1) 0%, rgba(197,160,89,0.1) 50%, rgba(6,182,212,0.1) 100%)',
              border: '1px solid rgba(236,72,153,0.3)',
              borderRadius: '12px',
              padding: '14px 18px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '38px', height: '38px', borderRadius: '10px',
                background: 'rgba(236,72,153,0.2)', border: '1px solid rgba(236,72,153,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ec4899', flexShrink: 0
              }}>
                <Sparkles size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 700, color: '#fff' }}>
                  Crea planes de estudio completos a partir de tus documentos
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.4 }}>
                  Sube un archivo oficial (PDF, DOCX o TXT) o pega el texto normativo. La IA estructurará automáticamente los temas, lecciones didácticas, puntos clave, preguntas de evaluación y tarjetas de repaso para guardarlos directamente en Firestore.
                </p>
              </div>
            </div>

            {/* SECCIÓN 1: SUBIR DOCUMENTO / PEGAR TEXTO */}
            <div style={{ ...S.card, padding: '16px 18px', background: 'rgba(7,21,38,0.6)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={S.label}>1. Documento o Material de Estudio Fuente</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt"
                    onChange={handleBulkFileSelect}
                     style={{  display: 'none'  }} className="adm-input"
                  />
                  <button
                    type="button"
                    onClick={() => bulkFileInputRef.current?.click()}
                    disabled={bulkAiExtracting || bulkAiGenerando}
                    style={{
                      ...S.btnSecondary,
                      borderColor: 'rgba(56,189,248,0.5)',
                      color: '#38bdf8',
                      padding: '6px 12px',
                      fontSize: '0.74rem',
                    }}
                  >
                    {bulkAiExtracting ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
                    <span>{bulkAiExtracting ? 'Extrayendo texto...' : bulkAiFileName ? `Cambiar archivo (${bulkAiFileName})` : '📁 Subir PDF / DOCX / TXT'}</span>
                  </button>
                  {bulkAiText && (
                    <button
                      type="button"
                      onClick={() => { setBulkAiText(''); setBulkAiFileName(''); setBulkAiFile(null); }}
                      style={{ ...S.btnDanger, padding: '6px 10px', fontSize: '0.7rem' }}
                    >
                      <Trash2 size={12} /> <span>Limpiar</span>
                    </button>
                  )}
                </div>
              </div>

              {bulkAiFileName && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px',
                  background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.3)',
                  borderRadius: '8px', marginBottom: '10px', fontSize: '0.75rem', color: '#38bdf8',
                }}>
                  <CheckCircle2 size={14} />
                  <span>Archivo cargado: <strong>{bulkAiFileName}</strong></span>
                </div>
              )}

              <textarea
                rows={6}
                value={bulkAiText}
                onChange={(e) => setBulkAiText(e.target.value)}
                style={{
                  ...S.input,
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                }}
                placeholder="Pega aquí el contenido del documento, manuales navales, leyes, reglamentos o temario que servirá de base para la generación..."
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '0.68rem', color: '#64748b' }}>
                <span>{bulkAiText ? `${bulkAiText.length.toLocaleString()} caracteres (~${Math.round(bulkAiText.split(/\s+/).filter(Boolean).length).toLocaleString()} palabras)` : 'Sin contenido aún'}</span>
                <span>Límite recomendado: hasta 50,000 caracteres por lote</span>
              </div>
            </div>

            {/* SECCIÓN 2: CONFIGURACIÓN DE DESTINO, PARÁMETROS Y MODELO */}
            <div style={{ ...S.card, padding: '16px 18px', background: 'rgba(7,21,38,0.6)' }}>
              <span style={{ ...S.label, marginBottom: '12px' }}>2. Configuración Pedagógica y Parámetros de IA</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
                {/* Área destino */}
                <div>
                  <label style={S.label} htmlFor="bulk-area-sel">Área Académica de Destino</label>
                  <select
                    id="bulk-area-sel"
                    value={bulkAiArea}
                    onChange={(e) => setBulkAiArea(e.target.value)}
                    style={S.input}
                  >
                    {GENERAL_AREAS.map((a) => (
                      <option key={a.key} value={a.key}>{a.label}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.64rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                    Colección: vortexAcademyAreas/{bulkAiArea}/temas
                  </span>
                </div>

                {/* Modelo de IA */}
                <div>
                  <label style={S.label} htmlFor="bulk-model-sel">Modelo de Inteligencia Artificial</label>
                  <select
                    id="bulk-model-sel"
                    value={bulkAiSelectedModel}
                    onChange={(e) => setBulkAiSelectedModel(e.target.value)}
                    style={S.input}
                  >
                    {/* Opciones dinámicas desde Firebase */}
                    {Object.keys(bulkAiAvailableModels).length > 0
                      ? Object.entries(bulkAiAvailableModels).map(([k, m]) => (
                          <option key={k} value={k}>
                            {m.displayName || m.modelName || k} ({m.provider || 'openrouter'})
                          </option>
                        ))
                      : (
                          <>
                            <option value="DeepSeek">DeepSeek V4 Flash (OpenRouter) ⭐ Recomendado</option>
                            <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (Vertex AI)</option>
                          </>
                        )
                    }
                  </select>
                  <span style={{ fontSize: '0.64rem', color: '#64748b', marginTop: '3px', display: 'block' }}>
                    Conexión directa con API Key (OpenRouter / Google AI)
                  </span>
                </div>

                {/* Cantidad de Temas */}
                <div>
                  <label style={S.label} htmlFor="bulk-num-temas">Cantidad de Temas a Generar</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      id="bulk-num-temas"
                      type="number"
                      min={1}
                      max={10}
                      value={bulkAiNumTemas}
                      onChange={(e) => setBulkAiNumTemas(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))}
                      style={S.input}
                    />
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>temas</span>
                  </div>
                </div>

                {/* Lecciones por Tema */}
                <div>
                  <label style={S.label} htmlFor="bulk-num-lessons">Lecciones por Cada Tema</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      id="bulk-num-lessons"
                      type="number"
                      min={1}
                      max={6}
                      value={bulkAiLessonsPerTema}
                      onChange={(e) => setBulkAiLessonsPerTema(Math.max(1, Math.min(6, parseInt(e.target.value, 10) || 1)))}
                      style={S.input}
                    />
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>lecciones c/u</span>
                  </div>
                </div>
              </div>

              {/* Asignación de Jerarquías / Grados */}
              <div style={{ marginTop: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label  style={{  ...S.label,  margin: 0  }} className="adm-label">Jerarquías / Grados con Acceso</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => setBulkAiGrados([...jerarquias])}
                      style={{ background: 'none', border: 'none', color: '#c5a059', fontSize: '0.66rem', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Marcar todas
                    </button>
                    <span style={{ color: '#64748b', fontSize: '0.66rem' }}>·</span>
                    <button
                      type="button"
                      onClick={() => setBulkAiGrados([])}
                      style={{ background: 'none', border: 'none', color: '#f87171', fontSize: '0.66rem', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      Desmarcar
                    </button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {jerarquias.map((j) => {
                    const active = bulkAiGrados.includes(j);
                    return (
                      <button
                        key={j}
                        type="button"
                        onClick={() => {
                          const updated = active
                            ? bulkAiGrados.filter((g) => g !== j)
                            : [...bulkAiGrados, j];
                          setBulkAiGrados(updated);
                        }}
                        style={{
                          padding: '4px 10px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700,
                          border: active ? '1px solid rgba(197,160,89,0.6)' : '1px solid rgba(255,255,255,0.12)',
                          background: active ? 'rgba(197,160,89,0.2)' : 'rgba(255,255,255,0.03)',
                          color: active ? '#d8b978' : '#94a3b8',
                        }}
                      >
                        {active ? '✓ ' : ''}{j}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* SECCIÓN 3: PROGRESO DE GENERACIÓN */}
            {(bulkAiGenerando || bulkAiSaving) && (
              <div style={{
                ...S.card, padding: '16px', background: 'rgba(197,160,89,0.1)',
                border: '1px solid rgba(197,160,89,0.4)', display: 'flex', flexDirection: 'column', gap: '10px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.78rem', color: '#c4b5fd' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}>
                    <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                    {bulkAiStep || 'Procesando con IA...'}
                  </span>
                  <span style={{ fontWeight: 800 }}>{bulkAiProgress}%</span>
                </div>
                <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.4)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${bulkAiProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #ec4899 0%, #c5a059 50%, #06b6d4 100%)',
                    transition: 'width 0.4s ease',
                  }} />
                </div>
              </div>
            )}

            {/* SECCIÓN 4: VISTA PREVIA INTERACTIVA DE TEMAS GENERADOS */}
            {bulkAiGeneratedTemas.length > 0 && (
              <div style={{ ...S.card, padding: '16px 18px', background: 'rgba(7,21,38,0.8)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle2 size={16} style={{ color: '#34d399' }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#fff' }}>
                      3. Vista Previa: {bulkAiGeneratedTemas.length} Temas Generados ({bulkAiGeneratedTemas.reduce((a, t) => a + (t.lessons?.length || 0), 0)} Lecciones)
                    </span>
                  </div>
                  <span style={S.badge('#34d399')}>Listo para guardar</span>
                </div>

                {/* Pestañas de Temas Generados */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', borderBottom: '1px solid rgba(197,160,89,0.15)', marginBottom: '14px' }}>
                  {bulkAiGeneratedTemas.map((t, idx) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setBulkAiPreviewTemaIndex(idx)}
                      style={{
                        padding: '7px 14px', borderRadius: '10px', cursor: 'pointer',
                        fontSize: '0.74rem', fontWeight: 700, whiteSpace: 'nowrap',
                        border: bulkAiPreviewTemaIndex === idx ? '1px solid #c5a059' : '1px solid rgba(255,255,255,0.1)',
                        background: bulkAiPreviewTemaIndex === idx ? 'rgba(197,160,89,0.25)' : 'rgba(255,255,255,0.03)',
                        color: bulkAiPreviewTemaIndex === idx ? '#fff' : '#94a3b8',
                      }}
                    >
                      {idx + 1}. {t.title.length > 25 ? t.title.substring(0, 25) + '...' : t.title} ({t.lessons?.length || 0})
                    </button>
                  ))}
                </div>

                {/* Detalle del Tema Seleccionado */}
                {(() => {
                  const currentTema = bulkAiGeneratedTemas[bulkAiPreviewTemaIndex];
                  if (!currentTema) return null;

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={S.label}>Título del Tema</label>
                          <input
                            type="text"
                            value={currentTema.title}
                            onChange={(e) => {
                              const updated = [...bulkAiGeneratedTemas];
                              updated[bulkAiPreviewTemaIndex] = { ...currentTema, title: e.target.value };
                              setBulkAiGeneratedTemas(updated);
                            }}
                            style={S.input}
                          />
                        </div>
                        <div>
                          <label style={S.label}>Módulo</label>
                          <input
                            type="text"
                            value={currentTema.module}
                            onChange={(e) => {
                              const updated = [...bulkAiGeneratedTemas];
                              updated[bulkAiPreviewTemaIndex] = { ...currentTema, module: e.target.value };
                              setBulkAiGeneratedTemas(updated);
                            }}
                            style={S.input}
                          />
                        </div>
                      </div>

                      {/* Lista de Lecciones del Tema */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
                        <span style={{ ...S.label, margin: 0 }}>Lecciones Contenidas ({currentTema.lessons?.length || 0})</span>
                        {currentTema.lessons?.map((l, lIdx) => (
                          <div
                            key={l.id || lIdx}
                            style={{
                              border: '1px solid rgba(197,160,89,0.2)',
                              borderRadius: '10px',
                              padding: '12px 14px',
                              background: 'rgba(10,25,41,0.7)',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                              <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#38bdf8' }}>
                                Lección {lIdx + 1}: {l.title}
                              </span>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <span style={S.badge('#c5a059')}>{l.paragraphs?.length || 0} párrafos</span>
                                <span style={S.badge('#f59e0b')}>{l.quizQuestions?.length || 0} preguntas</span>
                                <span style={S.badge('#34d399')}>{l.flashcards?.length || 0} flashcards</span>
                              </div>
                            </div>

                            {/* Párrafos */}
                            {l.paragraphs && l.paragraphs.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.4, background: 'rgba(0,0,0,0.2)', padding: '8px 10px', borderRadius: '6px' }}>
                                {l.paragraphs[0]}
                                {l.paragraphs.length > 1 && (
                                  <span style={{ color: '#64748b', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
                                    + {l.paragraphs.length - 1} párrafos adicionales generados...
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Puntos clave / Callout */}
                            {l.callout && (
                              <div style={{ fontSize: '0.7rem', color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '6px 10px', borderRadius: '6px' }}>
                                <strong>{l.callout.icon || 'ℹ️'} Nota:</strong> {l.callout.text}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {/* Modal: generación audiovisual por unidad (tema / módulo / lección) */}
      {/* (definido anteriormente) */}
    </div>
  );
};

export default AdminLectorTemas;


